import React from 'react';
import { useInView } from 'react-intersection-observer';
import { supabase } from '../lib/supabase';

export default function Waitlist() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [isExpanded, setIsExpanded] = React.useState(true);
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
  });
  const [status, setStatus] = React.useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const { error } = await supabase
        .from('waitlist')
        .insert([formData]);

      if (error) throw error;

      setStatus('success');
      setFormData({ name: '', email: '' });
    } catch (error) {
      console.error('Error:', error);
      setStatus('error');
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-transparent">
      <div 
        className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-md shadow-lg border-t border-white/20 dark:border-gray-700/50 cursor-pointer"
        style={{ backdropFilter: 'blur(12px)' }}
        onClick={() => !isExpanded && setIsExpanded(true)}
      >
        <div className={`container mx-auto px-4 ${isExpanded ? 'py-3' : 'py-1'} transition-all duration-300`}>
          <div className="max-w-lg mx-auto" ref={ref}>
            <div className="relative flex items-center justify-center mb-0">
              <div className={`text-center transition-all duration-700 ${
                inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}>
                <h2 className={`text-xl font-bold text-gray-900 dark:text-gray-100 transition-all duration-300 ${
                  isExpanded ? 'mb-3' : 'mb-0'
                }`}>
                  β版ユーザー募集中 🙌
                </h2>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
                className="absolute right-0 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              >
                <svg
                  className={`w-6 h-6 transform transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>

            <div 
              className={`overflow-hidden transition-all duration-300 ${
                isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <p className="text-sm text-gray-600 dark:text-gray-300 text-center mb-3">
                β版に順次ご招待します！👋
              </p>

              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="px-3 py-2 text-sm border border-white/20 dark:border-gray-600/50 rounded-lg focus:ring-2 focus:ring-gray-900/20 dark:focus:ring-gray-500/50 focus:border-transparent bg-white/40 dark:bg-gray-700/40 backdrop-blur-md text-gray-900 dark:text-gray-100"
                  style={{ backdropFilter: 'blur(12px)' }}
                  placeholder="お名前"
                  required
                />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="px-3 py-2 text-sm border border-white/20 dark:border-gray-600/50 rounded-lg focus:ring-2 focus:ring-gray-900/20 dark:focus:ring-gray-500/50 focus:border-transparent bg-white/40 dark:bg-gray-700/40 backdrop-blur-md text-gray-900 dark:text-gray-100"
                  style={{ backdropFilter: 'blur(12px)' }}
                  placeholder="メールアドレス"
                  required
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className={`group relative whitespace-nowrap px-4 py-2 text-sm font-medium rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 ${
                    status === 'loading' ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <div className={`absolute inset-0 bg-gradient-to-r from-primary-600/80 to-accent-500/80 backdrop-blur-md transition-all duration-300 ${
                    status !== 'loading' && 'group-hover:scale-[1.02] group-hover:brightness-110'
                  }`}></div>
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-20 bg-white transition-opacity duration-300"></div>
                  <div className="relative flex items-center justify-center gap-2 text-white transition-transform duration-300 group-hover:scale-[1.02]">
                    {status === 'loading' ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <span className="group-hover:tracking-wide transition-all duration-300">参加する</span>
                        <svg
                          className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 7l5 5m0 0l-5 5m5-5H6"
                          />
                        </svg>
                      </>
                    )}
                  </div>
                </button>
              </form>

              {status === 'success' && (
                <p className="text-green-600 text-center text-sm mt-2">
                  ご登録ありがとうございます！順次ご招待メールをお送りします。
                </p>
              )}

              {status === 'error' && (
                <p className="text-red-600 text-center text-sm mt-2">
                  エラーが発生しました。もう一度お試しください。
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
